import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import './TaskOverrideModal.css';

interface TaskOption {
  domain: string;
  task: string;
  description: string;
  confidence?: number;
}

interface TaskOverrideModalProps {
  isOpen: boolean;
  currentDomain?: string;
  currentTask?: string;
  onConfirm: (domain: string, task: string) => void;
  onCancel: () => void;
}

// Common task alternatives grouped by domain
const TASK_ALTERNATIVES: Record<string, TaskOption[]> = {
  treasury: [
    { domain: 'treasury', task: 'bank_reconciliation', description: 'Match bank transactions with GL entries' },
    { domain: 'treasury', task: 'cash_flow_forecast', description: 'Project future cash positions' },
    { domain: 'treasury', task: 'wire_transfer_validation', description: 'Verify wire transfer details' },
    { domain: 'treasury', task: 'check_register', description: 'Manage check disbursements' },
  ],
  accounts_receivable: [
    { domain: 'accounts_receivable', task: 'ar_aging', description: 'Analyze overdue customer balances' },
    { domain: 'accounts_receivable', task: 'invoice_matching', description: 'Match payments to invoices' },
    { domain: 'accounts_receivable', task: 'credit_memo_processing', description: 'Process credit adjustments' },
    { domain: 'accounts_receivable', task: 'collections_report', description: 'Track collection efforts' },
  ],
  accounts_payable: [
    { domain: 'accounts_payable', task: 'ap_aging', description: 'Track vendor payment due dates' },
    { domain: 'accounts_payable', task: 'vendor_reconciliation', description: 'Reconcile vendor statements' },
    { domain: 'accounts_payable', task: 'payment_approval', description: 'Review payments for approval' },
    { domain: 'accounts_payable', task: 'duplicate_detection', description: 'Find duplicate invoices' },
  ],
  general_ledger: [
    { domain: 'general_ledger', task: 'journal_entry_review', description: 'Review and validate journal entries' },
    { domain: 'general_ledger', task: 'account_reconciliation', description: 'Reconcile GL accounts' },
    { domain: 'general_ledger', task: 'trial_balance', description: 'Generate trial balance report' },
    { domain: 'general_ledger', task: 'consolidation', description: 'Consolidate multiple entities' },
  ],
  revenue: [
    { domain: 'revenue', task: 'revenue_recognition', description: 'Calculate revenue schedules' },
    { domain: 'revenue', task: 'deferred_revenue', description: 'Track unearned revenue' },
    { domain: 'revenue', task: 'sales_analysis', description: 'Analyze sales patterns' },
    { domain: 'revenue', task: 'commission_calculation', description: 'Calculate sales commissions' },
  ],
  expense: [
    { domain: 'expense', task: 'expense_categorization', description: 'Categorize expense transactions' },
    { domain: 'expense', task: 'budget_variance', description: 'Compare actuals to budget' },
    { domain: 'expense', task: 'cost_allocation', description: 'Allocate costs to departments' },
    { domain: 'expense', task: 'expense_approval', description: 'Review expense reports' },
  ],
};

const TaskOverrideModal: React.FC<TaskOverrideModalProps> = ({
  isOpen,
  currentDomain,
  currentTask,
  onConfirm,
  onCancel,
}) => {
  const [selectedDomain, setSelectedDomain] = useState<string>(currentDomain || '');
  const [selectedTask, setSelectedTask] = useState<string>(currentTask || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [customTask, setCustomTask] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  useEffect(() => {
    if (currentDomain) setSelectedDomain(currentDomain);
    if (currentTask) setSelectedTask(currentTask);
  }, [currentDomain, currentTask]);

  const handleConfirm = () => {
    if (useCustom && customTask) {
      // For custom tasks, use 'general_ledger' as default domain if not selected
      onConfirm(selectedDomain || 'general_ledger', customTask);
    } else if (selectedDomain && selectedTask) {
      onConfirm(selectedDomain, selectedTask);
    }
  };

  const filteredTasks = () => {
    const allTasks: TaskOption[] = [];
    Object.entries(TASK_ALTERNATIVES).forEach(([domain, tasks]) => {
      tasks.forEach(task => {
        allTasks.push(task);
      });
    });

    if (!searchQuery) {
      return selectedDomain ? TASK_ALTERNATIVES[selectedDomain] || [] : allTasks;
    }

    return allTasks.filter(
      task =>
        task.task.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.domain.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  const domains = Object.keys(TASK_ALTERNATIVES);

  if (!isOpen) return null;

  return (
    <Modal
      title="Override Task Identification"
      onClose={onCancel}
      className="task-override-modal"
      size="large"
    >
      <div className="override-modal-content">
        {/* Current Selection Display */}
        {currentDomain && currentTask && (
          <div className="current-selection">
            <div className="current-label">Current Identification:</div>
            <div className="current-value">
              <span className="current-domain">{currentDomain}</span>
              <span className="separator">→</span>
              <span className="current-task">{currentTask.replace(/_/g, ' ')}</span>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="override-search">
          <input
            type="text"
            placeholder="Search for a task..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
            autoFocus
          />
          <span className="search-icon">🔍</span>
        </div>

        {/* Domain Filter Tabs */}
        {!searchQuery && (
          <div className="domain-tabs">
            <button
              className={`domain-tab ${!selectedDomain ? 'active' : ''}`}
              onClick={() => setSelectedDomain('')}
            >
              All Domains
            </button>
            {domains.map(domain => (
              <button
                key={domain}
                className={`domain-tab ${selectedDomain === domain ? 'active' : ''}`}
                onClick={() => setSelectedDomain(domain)}
              >
                {domain.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        )}

        {/* Task Options */}
        <div className="task-options">
          <div className="options-header">
            <span>Select a task:</span>
            <span className="options-count">{filteredTasks().length} options</span>
          </div>
          
          <div className="task-list">
            {filteredTasks().map((taskOption) => (
              <div
                key={`${taskOption.domain}-${taskOption.task}`}
                className={`task-option ${
                  selectedTask === taskOption.task && selectedDomain === taskOption.domain
                    ? 'selected'
                    : ''
                }`}
                onClick={() => {
                  setSelectedDomain(taskOption.domain);
                  setSelectedTask(taskOption.task);
                  setUseCustom(false);
                }}
              >
                <div className="task-option-header">
                  <span className="task-domain-badge">{taskOption.domain.replace(/_/g, ' ')}</span>
                  <span className="task-name">{taskOption.task.replace(/_/g, ' ')}</span>
                </div>
                <div className="task-description">{taskOption.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Task Option */}
        <div className="custom-task-section">
          <div className="custom-header">
            <label className="custom-checkbox">
              <input
                type="checkbox"
                checked={useCustom}
                onChange={(e) => setUseCustom(e.target.checked)}
              />
              <span>Define custom task</span>
            </label>
          </div>
          
          {useCustom && (
            <div className="custom-inputs">
              <select
                value={selectedDomain}
                onChange={(e) => setSelectedDomain(e.target.value)}
                className="domain-select"
              >
                <option value="">Select domain...</option>
                {domains.map(domain => (
                  <option key={domain} value={domain}>
                    {domain.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
              
              <input
                type="text"
                placeholder="Enter custom task name..."
                value={customTask}
                onChange={(e) => setCustomTask(e.target.value)}
                className="custom-task-input"
              />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="override-actions">
          <button
            className="btn-confirm"
            onClick={handleConfirm}
            disabled={
              useCustom
                ? !customTask || !selectedDomain
                : !selectedTask || !selectedDomain
            }
          >
            Confirm Override
          </button>
          <button className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>

        {/* Help Text */}
        <div className="override-help">
          <p>
            💡 <strong>Tip:</strong> Select the task that best matches what you're trying to accomplish.
            The AI will use the appropriate framework and rules for the selected task.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default TaskOverrideModal;